import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, Stethoscope, FileText, Camera, PenTool,
  Lock, Eye, EyeOff, Save, X, Check
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';

const Profile = () => {
  const { print_lang } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    specialty: '',
    license_number: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await fetchWithAuth('/api/dentists/me');
      if (response.success) {
        setProfile(response.data);
        setFormData({
          name: response.data.name || '',
          phone: response.data.phone || '',
          specialty: response.data.specialty || '',
          license_number: response.data.license_number || ''
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignatureFile(file);
      setSignaturePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('phone', formData.phone);
      data.append('specialty', formData.specialty);
      data.append('license_number', formData.license_number);

      if (photoFile) {
        data.append('photo', photoFile);
      }
      if (signatureFile) {
        data.append('signature', signatureFile);
      }

      const response = await fetchWithAuth('/api/dentists/profile', {
        method: 'PUT',
        body: data
      });

      if (response.success) {
        setMessage({ type: 'success', text: print_lang('profile_updated', 'Profile updated successfully') });
        setEditMode(false);
        setPhotoFile(null);
        setSignatureFile(null);
        setPhotoPreview(null);
        setSignaturePreview(null);
        loadProfile();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || print_lang('failed_to_update', 'Failed to update profile') });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: print_lang('passwords_not_match', 'Passwords do not match') });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: print_lang('password_too_short', 'Password must be at least 6 characters') });
      return;
    }

    setSaving(true);
    try {
      const response = await fetchWithAuth('/api/dentists/change-password', {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.success) {
        setMessage({ type: 'success', text: print_lang('password_changed', 'Password changed successfully') });
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || print_lang('failed_to_change_password', 'Failed to change password') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 bg-white/20 rounded-full overflow-hidden">
                {photoPreview || profile?.images_path ? (
                  <img
                    src={photoPreview || `${API_URL}/${profile.images_path}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-10 h-10 text-white/50" />
                  </div>
                )}
              </div>
              {editMode && (
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-100">
                  <Camera className="w-4 h-4 text-gray-600" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">{profile?.name}</h2>
              <p className="text-blue-100">{profile?.specialty || print_lang('general_dentist', 'General Dentist')}</p>
              {profile?.email_verified && (
                <span className="inline-flex items-center gap-1 mt-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  <Check className="w-3 h-3" />
                  {print_lang('verified', 'Verified')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {print_lang('name', 'Full Name')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg
                  ${editMode ? 'focus:ring-2 focus:ring-blue-500' : 'bg-gray-50 cursor-not-allowed'}`}
              />
            </div>
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {print_lang('email', 'Email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {print_lang('phone', 'Phone')}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg
                  ${editMode ? 'focus:ring-2 focus:ring-blue-500' : 'bg-gray-50 cursor-not-allowed'}`}
              />
            </div>
          </div>

          {/* Specialty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {print_lang('specialty', 'Specialty')}
            </label>
            <div className="relative">
              <Stethoscope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                name="specialty"
                value={formData.specialty}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg appearance-none
                  ${editMode ? 'focus:ring-2 focus:ring-blue-500 bg-white' : 'bg-gray-50 cursor-not-allowed'}`}
              >
                <option value="">{print_lang('select_specialty', 'Select Specialty')}</option>
                <option value="general">General Dentistry</option>
                <option value="orthodontics">Orthodontics</option>
                <option value="endodontics">Endodontics</option>
                <option value="periodontics">Periodontics</option>
                <option value="prosthodontics">Prosthodontics</option>
                <option value="oral_surgery">Oral Surgery</option>
                <option value="pediatric">Pediatric Dentistry</option>
              </select>
            </div>
          </div>

          {/* License Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {print_lang('license_number', 'License Number')}
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="license_number"
                value={formData.license_number}
                onChange={handleChange}
                disabled={!editMode}
                className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg
                  ${editMode ? 'focus:ring-2 focus:ring-blue-500' : 'bg-gray-50 cursor-not-allowed'}`}
              />
            </div>
          </div>

          {/* Signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {print_lang('signature', 'Digital Signature')}
            </label>
            <div className="border border-gray-300 rounded-lg p-4">
              {signaturePreview || profile?.signature_path ? (
                <div className="relative">
                  <img
                    src={signaturePreview || `${API_URL}/${profile.signature_path}`}
                    alt="Signature"
                    className="max-h-20 mx-auto"
                  />
                  {editMode && (
                    <label className="absolute top-0 right-0 w-8 h-8 bg-blue-600 rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-blue-700">
                      <PenTool className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleSignatureChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              ) : editMode ? (
                <label className="flex flex-col items-center justify-center py-4 cursor-pointer hover:bg-gray-50 rounded-lg">
                  <PenTool className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">{print_lang('upload_signature', 'Upload Signature')}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSignatureChange}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="text-center py-4 text-gray-400">
                  {print_lang('no_signature', 'No signature uploaded')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 bg-gray-50 border-t flex justify-between">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Lock className="w-4 h-4" />
            {print_lang('change_password', 'Change Password')}
          </button>

          {editMode ? (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditMode(false);
                  setFormData({
                    name: profile?.name || '',
                    phone: profile?.phone || '',
                    specialty: profile?.specialty || '',
                    license_number: profile?.license_number || ''
                  });
                  setPhotoFile(null);
                  setSignatureFile(null);
                  setPhotoPreview(null);
                  setSignaturePreview(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {print_lang('cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <div className="spinner w-4 h-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {print_lang('save', 'Save')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {print_lang('edit', 'Edit')}
            </button>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 animate-slideUp">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{print_lang('change_password', 'Change Password')}</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('current_password', 'Current Password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('new_password', 'New Password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('confirm_password', 'Confirm Password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <div className="spinner w-5 h-5 border-2 border-white border-t-transparent mx-auto"></div>
                  ) : (
                    print_lang('change_password', 'Change Password')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
